import {DOCUMENT} from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter, HostBinding,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import {fromEvent, Subject} from 'rxjs';
import {filter, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {trigger, transition, style, animate} from "@angular/animations";
import {Observable} from "rxjs";
import {TransitionHandler} from "./transition-handler";
import {scaleIn, scaleOut} from "./modal.animations";
import {isNumber, isString} from "../util/util";

// Define animation duration
const duration = 200; // Match the duration in modal.animations.ts

@Component({
    selector: 'db-modal-window',
    template: `
    <div #dialog [class]="'modal-dialog' + ((mobileView || fullHeight) && !fixMobile ? ' full-screen' : '') + (hasOutsideLeftCloseBtn || hasOutsideRightCloseBtn ? ' position-relative' : '')" (window:resize)="onResize()">
      <i [class]="'modal-close modal-close-outside db-icon db-icon-ds-close-bg' + (whiteCloseBtn ? ' white' : '')"
         [style.display]="hasOutsideCloseBtn ? '' : 'none'"
         [style.opacity]="opacity ? opacity : ''"></i>
      <i [class]="'modal-close modal-close-left db-icon db-icon-ds-close-small-arrow'"
         [style.display]="hasOutsideLeftCloseBtn ? '' : 'none'"
         [style.opacity]="opacity ? opacity : ''"></i>
      <i [class]="'modal-close modal-close-right db-icon db-icon-ds-close-small-arrow'"
         [style.display]="hasOutsideRightCloseBtn ? '' : 'none'"
         [style.opacity]="opacity ? opacity : ''"></i>
      <div [class]="'modal-content' + (!hasBorder ? ' without-border' : '') + (!!overflowHidden ? ' overflow-hidden' : '')"
           [@animationTrigger]="animation"
           [style.max-height]="!mobileView && !fullHeight ? ('calc(100vh - 20px)') : ''"
           [style.height]="getHeight()"
           [style.width]="width && !mobileView ? (width + 'px') : ''"
           [style.transition]="!mobileView ? 'all 300ms linear' : ''">
        <i [class]="'modal-close db-icon db-icon-ds-close-small-arrow' + (whiteCloseBtn ? ' white' : '')"
           [style.display]="hasCloseBtn ? '' : 'none'"
           [style.opacity]="opacity ? opacity : ''"
           [class.paid]="isPaidFeature"
           (click)="dismiss(0)"></i>
        <i [class]="'modal-close db-icon db-icon-ds-close-small-arrow' + (whiteCloseBtn ? ' white' : '')"
           [style.display]="hasCloseSharpBtn ? '' : 'none'"
           [style.opacity]="opacity ? opacity : ''"
           [class.paid]="isPaidFeature"
           (click)="dismiss(0)"></i>
        <ng-content></ng-content>
      </div>
    </div>
  `,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./modal-window.scss'],
    animations: [
        trigger('animationTrigger', [
            transition('void => scale', scaleIn),
            transition('scale => void', scaleOut),
            transition('void => fade', [
                style({ transform: 'scale(0.9)', opacity: 0 }),
                animate(duration, style({ transform: 'scale(1)', opacity: 1 }))
            ]),
            transition('fade => void', [
                style({ transform: 'scale(1)', opacity: 1 }),
                animate(duration, style({ transform: 'scale(0.9)', opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class DbModalWindowComponent implements OnInit, OnDestroy {

  private _closed$ = new Subject<void>();
  private _mobileView: boolean = window.innerWidth <= 991;

  get mobileView() { return this._mobileView; }

  @ViewChild('dialog'/*, {static: true}*/) private _dialogEl: ElementRef<HTMLElement>;

  @Input() height: number | string;
  @Input() width: number | 'auto';
  @Input() hasCloseBtn: boolean;
  @Input() isPaidFeature: boolean;
  @Input() hasOutsideCloseBtn: boolean;
  @Input() hasOutsideLeftCloseBtn: boolean;
  @Input() hasOutsideRightCloseBtn: boolean;
  @Input() hasCloseSharpBtn: boolean;
  @Input() hasBorder: boolean;
  @Input() whiteCloseBtn: boolean;
  @Input() fixMobile: boolean;
  @Input() opacity: number = 1;
  @Input() backdrop: boolean | 'static';
  @Input() overflowHidden: boolean = true;
  @Input() readonly fullHeight: boolean;
  @Input() animation: 'scale' | 'fade';
  @HostBinding('class') @Input() position: 'top' | 'center' | 'bottom' | 'dynamic';

  @Output() dismissEvent = new EventEmitter();

  constructor(
    @Inject(DOCUMENT) private _document: any,
    private _elRef: ElementRef<HTMLElement>,
    private _zone: NgZone,
    private _transitionHandler: TransitionHandler
  ) {}

  dismiss(reason): void {
    this.dismissEvent.emit(reason);
  }

  ngOnInit() {
    this._zone.onStable.asObservable().pipe(take(1)).subscribe(() => {
      this._enableEventHandling();
    });
  }

  ngOnDestroy() {
    this._disableEventHandling();
  }

  getHeight(): string {
    if (typeof this.height === 'string') {
      return this.height;
    } else if (this.height && !this.mobileView) {
      return `${this.height}px`;
    }
    return '';
  }

  hide(): Observable<any> {
    const {nativeElement} = this._dialogEl;

    // When 'fade' is specified, use a combined animation
    let animationToUse;
    if (this.animation === 'fade') {
      // Combined fade and scale animation
      animationToUse = [
        style({ transform: 'scale(1)', opacity: 1 }),
        animate(duration, style({ transform: 'scale(0.9)', opacity: 0 }))
      ];
    } else {
      // Default scale animation
      animationToUse = scaleOut;
    }

    const transition$ = this._transitionHandler.createTransition(
      nativeElement,
      animationToUse,
      true
    );
    this._disableEventHandling();

    return transition$;
  }

  public onResize(): void {
    this._mobileView = window.innerWidth <= 991;
  }

  private _enableEventHandling() {
    const {nativeElement} = this._elRef;
    this._zone.runOutsideAngular(() => {

      // We're listening to 'mousedown' and 'mouseup' to prevent modal from closing when pressing the mouse
      // inside the modal dialog and releasing it outside
      let preventClose = false;
      fromEvent<MouseEvent>(this._dialogEl.nativeElement, 'mousedown')
        .pipe(
          takeUntil(this._closed$),
          tap(() => preventClose = false),
          switchMap(() => fromEvent<MouseEvent>(nativeElement, 'mouseup')
            .pipe(
              takeUntil(this._closed$),
              take(1)
            )
          ),
          filter(({target}) => nativeElement === target)
        )
        .subscribe(() => {
          preventClose = true;
        });

      // We're listening to 'click' to dismiss modal on modal window click, except when:
      // 1. clicking on modal dialog itself
      // 2. closing was prevented by mousedown/up handlers
      // 3. clicking on scrollbar when the viewport is too small and modal doesn't fit (click is not triggered at all)
      fromEvent<MouseEvent>(nativeElement, 'click')
        .pipe(
          takeUntil(this._closed$)
        )
        .subscribe(({target}) => {
          if (nativeElement === target) {
            if (this.backdrop === 'static') {
              // this._bumpBackdrop();
            } else if (this.backdrop === true && !preventClose) {
              this._zone.run(() => this.dismiss(0));
            }
          }

          preventClose = false;
        });
    });
  }

  private _disableEventHandling() {
    this._closed$.next();
  }

  /*private _bumpBackdrop() {
    if (this.backdrop === 'static') {
      const {nativeElement} = this._dialogEl;
      this._transitionHandler.createTransition(nativeElement, shake, this.animation);
    }
  }*/
  protected readonly isNumber = isNumber;
  protected readonly isString = isString;
}
